import type { UserRole } from '@compass/client/types/membership';
import cookies from 'js-cookie';

/**
 * Cookie Isolation Strategy:
 *
 * Cookies are kept isolated per subdomain to maintain separate authentication sessions.
 * This means:
 * - qa-dash.100ms.ai has its own authentication cookies
 * - dash.100ms.ai has separate authentication cookies
 * - Users need to log in separately on each subdomain
 *
 * Benefits:
 * - Complete isolation between environments (qa, staging, production)
 * - No risk of session bleeding between environments
 * - More secure as each environment is completely independent
 * - Easier to test different authentication states per environment
 */
export const createCookieKey = () => {
  // Use a consistent cookie name across all environments
  // Since cookies are isolated per subdomain by default (no domain attribute),
  // we don't need environment-specific names
  return 'compass_auth_token';
};

export const currentUser = () => {
  let authUser;
  try {
    const cookieKey = createCookieKey();
    const data = cookies.get(cookieKey) as string | null;
    authUser = JSON.parse(data || '');
  } catch {
    authUser = null;
  }
  return authUser;
};

export const saveCurrentUser = (data: any, expiryTime = 99) => {
  const cookieValue = JSON.stringify(data);
  const cookieKey = createCookieKey();
  const expirationTime = expiryTime;
  cookies.set(cookieKey, cookieValue, {
    expires: expirationTime,
    path: '/',
    sameSite: 'lax',
    secure: window.location.protocol === 'https:',
    // No domain attribute - cookie will be isolated to current subdomain
  });
};

export const removeCurrentUser = () => {
  const cookieKey = createCookieKey();
  clearLocalStorage();

  // Remove the cookie
  cookies.remove(cookieKey, { path: '/' });
};

const clearLocalStorage = () => {
  localStorage.clear();
};

/**
 * Filter user roles based on organization and program context.
 *
 * Priority order:
 * 1. Exact match: organization_id AND program_id both match
 * 2. Organization match: organization_id matches (program_id is undefined or null in role)
 * 3. Global roles: both organization_id and program_id are undefined/null in role
 *
 * @param roles - Array of user roles to filter
 * @param organizationId - Current organization ID
 * @param programId - Current program ID
 * @returns Filtered array of roles applicable to the current context
 */
export const filterRolesByContext = (
  roles: UserRole[],
  organizationId?: string | null,
  programId?: string | null,
): UserRole[] => {
  if (!roles || roles.length === 0) {
    return [];
  }

  return roles.filter(role => {
    // Exact match: both organization and program IDs match
    if (organizationId && programId) {
      if (role.organization_id === organizationId && role.program_id === programId) {
        return true;
      }
    }

    // Organization-level match: organization matches but role has no program scope
    if (organizationId && role.organization_id === organizationId && !role.program_id) {
      return true;
    }

    // Global roles: no organization or program scope
    if (!role.organization_id && !role.program_id) {
      return true;
    }

    return false;
  });
};

export const hasRoleSlug = (roles: UserRole[] | null | undefined, roleSlug: string): boolean => {
  if (!roles?.length || !roleSlug) {
    return false;
  }

  const normalizedRoleSlug = roleSlug.toLowerCase();
  return roles.some(role => role.slug?.toLowerCase() === normalizedRoleSlug);
};
